import { isQuery, IsQuery, Query } from '../../query';
import { JoinArgs, JoinCallback, JoinFirstArg, JoinQueryBuilder } from './join';
import { pushQueryArrayImmutable } from '../../query.utils';
import { ColumnsShape } from '../../../columns/columns-shape';
import { Column } from '../../../columns/column';
import { getIsJoinSubQuery } from '../../sql/get-is-join-sub-query';
import { prepareSubQueryForSql } from '../../internal-features/sub-query/sub-query-for-sql';
import { returnArg } from '../../../utils';
import { RelationJoinQuery } from '../../relations';
import {
  PickQueryQ,
  PickQueryRelationQueries,
  PickQueryRelations,
} from '../../pick-query-types';
import { _clone } from '../clone/clone';
import { JoinItemArgs } from './join.sql';
import { JoinedShapes, QueryData, QueryDataJoinTo } from '../../query-data';

/**
 * Processes arguments of join {@link JoinArgs} into {@link JoinItemArgs} type for building sql.
 * Resolves join callback.
 * Detects if the join should be an implicit lateral join.
 *
 * @param joinTo - main query
 * @param joinKey - joining as
 * @param first - first join argument
 * @param args - rest join arguments
 * @param joinSubQuery - callee should find out whether first argument should result in a sub-queried join
 * @param shape - aliased shape of a joined table, the one from `getShapeFromSelect`
 * @param whereExists - the lateral expression should be never wrapped into a sub query for `whereExist`
 * @param forbidLateral - LATERAL with a query that references the main table is not available in `updateFrom`
 */
export const processJoinArgs = (
  joinTo: Query,
  first: JoinFirstArg<never>,
  args:
    | [JoinCallback<Query, JoinFirstArg<Query>>]
    | JoinArgs<Query, JoinFirstArg<Query>>,
  joinSubQuery: boolean,
  shape: Column.QueryColumns | undefined,
  whereExists?: boolean,
  joinKey?: string,
  forbidLateral?: boolean,
): JoinItemArgs => {
  if (typeof first === 'string') {
    if (first in joinTo.relations) {
      const rel = joinTo.relations[first];
      let relQuery = rel.query as Query;

      // clone in case the callback changes alias
      if (typeof args[0] === 'function') relQuery = _clone(relQuery);

      const j = rel.joinQuery(relQuery, joinTo) as Query;
      if (typeof args[0] === 'function') {
        const r = args[0](
          makeJoinQueryBuilder(j, j.q.joinedShapes, joinTo, shape) as never,
        ) as Query;

        if (r.q.as !== joinKey) {
          relQuery.q.as = r.q.as;
        }

        return {
          j: j.merge(r),
          s: whereExists ? false : joinSubQuery || getIsJoinSubQuery(r),
          r,
        };
      }

      return { j, s: joinSubQuery };
    } else if (typeof args[0] !== 'function') {
      return { w: first, a: args as never };
    } else {
      const joinToQ = joinTo.q;
      const w = joinToQ.withShapes?.[first];
      if (!w) {
        throw new Error('Cannot find a `with` statement');
      }

      const j = joinTo.qb.baseQuery.clone();
      j.table = first;
      j.q = {
        shape: w.shape,
        runtimeComputeds: w.computeds,
        adapter: joinToQ.adapter,
        handleResult: joinToQ.handleResult,
        returnType: 'all',
        logger: joinToQ.logger,
      } as QueryData;
      j.baseQuery = j as Query;

      const joinedShapes = {
        ...joinToQ.joinedShapes,
        [(joinToQ.as || joinTo.table) as string]: joinTo.shape,
      } as JoinedShapes;

      const r = prepareSubQueryForSql(
        joinTo,
        args[0](
          makeJoinQueryBuilder(
            j,
            j.q.joinedShapes
              ? {
                  ...j.q.joinedShapes,
                  ...joinedShapes,
                }
              : joinedShapes,
            joinTo,
            shape,
          ) as never,
        ) as Query,
      );

      return {
        w: first,
        r,
        s: whereExists ? false : joinSubQuery || getIsJoinSubQuery(r),
      };
    }
  }

  const args0 = args.length ? args[0] : returnArg;
  if (typeof args0 === 'function') {
    let q = first as Query & {
      joinQueryAfterCallback?: RelationJoinQuery;
    };

    if (q.joinQueryAfterCallback) {
      let base = q.baseQuery;
      if (q.q.as) {
        base = base.as(q.q.as);
      }

      const { q: query } = q.joinQueryAfterCallback(
        base,
        joinTo,
      ) as unknown as PickQueryQ;

      if (query.and || query.or || query.scopes) {
        q = _clone(q);
        if (query.and) {
          pushQueryArrayImmutable(q, 'and', query.and);
        }
        if (query.or) {
          pushQueryArrayImmutable(q, 'or', query.or);
        }
        if (query.scopes) {
          q.q.scopes = { ...q.q.scopes, ...query.scopes };
        }
      }
    }

    const joinedShapes = {
      ...joinTo.q.joinedShapes,
      [(joinTo.q.as || joinTo.table) as string]: joinTo.shape,
    } as JoinedShapes;

    const r = args0(
      makeJoinQueryBuilder(
        q,
        q.q.joinedShapes
          ? {
              ...q.q.joinedShapes,
              ...joinedShapes,
            }
          : joinedShapes,
        joinTo,
        shape,
      ) as never,
    ) as Query;

    joinSubQuery ||= getIsJoinSubQuery(r);
    return {
      q: joinSubQuery && !forbidLateral ? q.merge(r) : q,
      r,
      s: joinSubQuery,
    };
  }

  return {
    q: first,
    a: args,
    s: joinSubQuery,
  } as never;
};

export const preprocessJoinArg = (
  q: PickQueryRelations,
  arg: JoinFirstArg<never>,
) => {
  if (typeof arg === 'function') {
    arg = arg(
      (q as unknown as PickQueryRelationQueries).relationQueries as never,
    );

    (
      arg as unknown as { joinQueryAfterCallback: unknown }
    ).joinQueryAfterCallback = (
      arg as unknown as { joinQuery: unknown }
    ).joinQuery;
  }

  return isQuery(arg) ? prepareSubQueryForSql(q as Query, arg as Query) : arg;
};

/**
 * Creates {@link JoinQueryBuilder} argument for join callback.
 *
 * @param joinedQuery - the query that is joining
 * @param joinedShapes
 * @param joinTo
 * @param shape - in `updateFrom` the columns of 2nd callback are aliased
 */
const makeJoinQueryBuilder = (
  joinedQuery: IsQuery,
  joinedShapes: JoinedShapes | undefined,
  joinTo: QueryDataJoinTo,
  shape: Column.QueryColumns | undefined,
): JoinQueryBuilder<Query, Query> => {
  const q = (joinedQuery as Query).baseQuery.clone();
  q.baseQuery = q;
  q.q.as = (joinedQuery as Query).q.as;
  q.q.joinedShapes = joinedShapes;
  q.q.joinTo = joinTo;

  // scopes are applied when handling 1st join argument,
  // prevent them to be applied a second time by a callback.
  if (q.q.scopes) {
    q.q.scopes = undefined;
  }

  if (shape) {
    q.q.shape = shape as ColumnsShape;
  }
  return q as never;
};
